import { ref, readonly } from 'vue'

export interface PendingAction {
  type: 'addToRecipeBook' | 'cloneRecipe' | 'generateRecipe' | 'createPantry' | 'other'
  payload?: Record<string, unknown>
  message?: string
}

const pendingAction = ref<PendingAction | null>(null)
const showLoginPrompt = ref(false)

/**
 * Composable for managing pending actions that require authentication.
 * 
 * Usage:
 * 1. Before an action that requires auth, call `setPendingAction()` with the action details
 * 2. Show the login prompt with `promptLogin()`
 * 3. After successful login, call `executePendingAction()` to get and clear the pending action
 */
export function usePendingAction() {
  /**
   * Set a pending action to be executed after login
   */
  const setPendingAction = (action: PendingAction) => {
    pendingAction.value = action
  }

  /**
   * Clear any pending action
   */
  const clearPendingAction = () => {
    pendingAction.value = null
  }

  /**
   * Show the login prompt dialog
   */
  const promptLogin = (action?: PendingAction) => {
    if (action) {
      setPendingAction(action)
    }
    showLoginPrompt.value = true
  }

  /**
   * Hide the login prompt dialog
   */
  const hideLoginPrompt = () => {
    showLoginPrompt.value = false
  }

  /**
   * Get and clear the pending action (call after successful login)
   */
  const consumePendingAction = (): PendingAction | null => {
    const action = pendingAction.value
    pendingAction.value = null
    return action
  }

  /**
   * Check if there's a pending action
   */
  const hasPendingAction = () => {
    return pendingAction.value !== null
  }

  /**
   * Get the current pending action without clearing it
   */
  const getPendingAction = () => {
    return readonly(pendingAction)
  }

  return {
    pendingAction: readonly(pendingAction),
    showLoginPrompt: readonly(showLoginPrompt),
    setPendingAction,
    clearPendingAction,
    promptLogin,
    hideLoginPrompt,
    consumePendingAction,
    hasPendingAction,
    getPendingAction
  }
}
